var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import * as React from 'react';
import { useMemo, useState } from 'react';
import { ChoiceGroup, IconButton, PersonaSize, Pivot, PivotItem, SearchBox, Stack, Text, TooltipHost, } from '@fluentui/react';
import ResourceInfoCard from '../shared/ResourceInfoCard';
import { filterAssignments, filterEngagements } from './dseMetrics';
const viewOptions = [
    { key: 'Active', text: 'Active' },
    { key: 'Historic', text: 'Historic' },
];
const thStyle = {
    textAlign: 'left',
    borderBottom: '1px solid #edebe9',
    padding: '8px',
    fontWeight: 600,
    fontSize: 12,
    whiteSpace: 'nowrap',
};
const tdStyle = {
    borderBottom: '1px solid #f3f2f1',
    padding: '8px',
    fontSize: 13,
    whiteSpace: 'nowrap',
};
const tableView = (key) => key === 'Historic' ? 'Historic' : 'Active';
const Metric = ({ label, value, tooltip }) => (React.createElement(Stack, { tokens: { childrenGap: 4 }, styles: {
        root: {
            minWidth: 180,
            maxWidth: 260,
            flex: '1 1 180px',
            padding: 12,
            border: '1px solid #edebe9',
            borderRadius: 2,
        },
    } },
    React.createElement(Stack, { horizontal: true, verticalAlign: "center", tokens: { childrenGap: 2 } },
        React.createElement(Text, { variant: "small", styles: { root: { color: '#605e5c' } } }, label),
        React.createElement(TooltipHost, { content: tooltip },
            React.createElement(IconButton, { iconProps: { iconName: 'Info' }, title: `${label} information`, ariaLabel: `${label} information`, styles: { root: { width: 24, height: 24 } } }))),
    React.createElement(Text, { variant: "large", title: typeof value === 'string' ? value : undefined, styles: {
            root: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        } }, value)));
const assignmentHeaders = [
    'Signed',
    'Assignment Name',
    'Allocated Hours',
    'Allocated Hours (Annual)',
    '% Sold Hours',
    'Role',
    'Start Date',
    'End Date',
    'Duration (Days)',
    'Remaining Days',
    'Delivery Status',
    'Country',
];
const engagementHeaders = [
    'Title',
    'Package ID',
    'Package Name',
    'Customer',
    'Hours Sold',
    'Hours Consumed',
    'Start Date',
    'End Date',
    'Delivery Rate %',
    'Remaining Days',
    'Owner',
    'Country',
    'CSAM',
    'Agreement ID',
];
export const DSEBreakdown = ({ resourceName, alias, contractHours, assignments, engagements, metrics, staffPreference, showPersona = true, profileHref, photoService, }) => {
    const [assignmentView, setAssignmentView] = useState('Active');
    const [assignmentSearch, setAssignmentSearch] = useState('');
    const [engagementView, setEngagementView] = useState('Active');
    const [engagementSearch, setEngagementSearch] = useState('');
    const filteredAssignments = useMemo(() => filterAssignments(assignments, assignmentView, assignmentSearch), [assignments, assignmentView, assignmentSearch]);
    const filteredEngagements = useMemo(() => filterEngagements(engagements, engagementView, engagementSearch), [engagements, engagementView, engagementSearch]);
    return (React.createElement(Stack, { tokens: { childrenGap: 16 }, styles: { root: { padding: 20, minWidth: 0 } }, "data-testid": "dse-breakdown" },
        showPersona ? (React.createElement(ResourceInfoCard, { alias: alias, name: resourceName, size: PersonaSize.size72, profileHref: profileHref, photoService: photoService })) : null,
        React.createElement(Pivot, { "aria-label": "DSE capacity and engagement details" },
            React.createElement(PivotItem, { headerText: "Capacity Hours & Assignments", itemCount: assignments.length },
                React.createElement(Stack, { tokens: { childrenGap: 16 }, styles: { root: { paddingTop: 12 } } },
                    React.createElement(Stack, { horizontal: true, wrap: true, tokens: { childrenGap: 12 }, "data-testid": "dse-metrics", "aria-label": `Capacity metrics for ${contractHours} EDE capacity hours` },
                        React.createElement(Metric, { label: "Committed Hours", value: metrics.committed, tooltip: "Sum of delivery hours allocated in active signed assignments." }),
                        React.createElement(Metric, { label: "Forecasted Hours", value: metrics.forecasted, tooltip: "Estimated hours assigned across active open requests." }),
                        React.createElement(Metric, { label: "Uncommitted Hours", value: metrics.uncommitted, tooltip: "Capacity hours minus committed annualized hours." }),
                        React.createElement(Metric, { label: "% Committed", value: `${metrics.perCommitted}%`, tooltip: "Committed annualized hours as a percentage of capacity hours." }),
                        React.createElement(Metric, { label: "Staff Preferences", value: staffPreference || 'Not available', tooltip: "Primary skills supplied by OneProfile." }),
                        React.createElement(Metric, { label: "Committed Hours (Annual)", value: metrics.committedAnnual, tooltip: "Annualized hours committed through active signed assignments." }),
                        React.createElement(Metric, { label: "Forecasted Hours (Annual)", value: metrics.forecastedAnnual, tooltip: "Annualized estimated hours in active unsigned assignments." }),
                        React.createElement(Metric, { label: "Unplanned Hours", value: metrics.unplanned, tooltip: "Capacity hours minus committed and forecasted annualized hours." }),
                        React.createElement(Metric, { label: "% Planned", value: `${metrics.perPlanned}%`, tooltip: "Committed plus forecasted annualized hours as a percentage of capacity." })),
                    React.createElement(Stack, { horizontal: true, horizontalAlign: "space-between", verticalAlign: "end", wrap: true, tokens: { childrenGap: 12 } },
                        React.createElement(ChoiceGroup, { label: "Assignment activity", selectedKey: assignmentView, options: viewOptions, styles: { flexContainer: { display: 'flex', gap: 12 } }, onChange: (_event, option) => {
                                if (option) {
                                    setAssignmentView(tableView(option.key));
                                    setAssignmentSearch('');
                                }
                            } }),
                        React.createElement(SearchBox, { ariaLabel: "Search assignment table", placeholder: "Search assignments", value: assignmentSearch, styles: { root: { width: 280 } }, onChange: (_event, value) => setAssignmentSearch(value !== null && value !== void 0 ? value : '') })),
                    React.createElement("div", { style: { overflowX: 'auto' } },
                        React.createElement("table", { "data-testid": "assignments-table", "aria-label": `${assignmentView} DSE assignments`, style: { width: '100%', borderCollapse: 'collapse' } },
                            React.createElement("thead", null,
                                React.createElement("tr", null, assignmentHeaders.map((header) => (React.createElement("th", { key: header, style: thStyle }, header))))),
                            React.createElement("tbody", null, filteredAssignments.length === 0 ? (React.createElement("tr", null,
                                React.createElement("td", { colSpan: assignmentHeaders.length, style: tdStyle },
                                    "No ",
                                    assignmentView.toLowerCase(),
                                    " assignments found."))) : (filteredAssignments.map((assignment, index) => (React.createElement("tr", { key: `${assignment.demandSourceId}-${index}` },
                                React.createElement("td", { style: tdStyle }, assignment.isSigned ? 'Yes' : 'No'),
                                React.createElement("td", { style: tdStyle }, assignment.assignmentName),
                                React.createElement("td", { style: tdStyle }, assignment.allocHours),
                                React.createElement("td", { style: tdStyle }, assignment.allocHoursAnnual),
                                React.createElement("td", { style: tdStyle }, assignment.perSold === 'N/A'
                                    ? 'N/A'
                                    : `${assignment.perSold}%`),
                                React.createElement("td", { style: tdStyle }, assignment.role),
                                React.createElement("td", { style: tdStyle }, assignment.startDate),
                                React.createElement("td", { style: tdStyle }, assignment.endDate),
                                React.createElement("td", { style: tdStyle }, assignment.duration),
                                React.createElement("td", { style: tdStyle }, assignment.remainingDays),
                                React.createElement("td", { style: tdStyle }, assignment.deliveryStatus),
                                React.createElement("td", { style: tdStyle }, assignment.country)))))))))),
            React.createElement(PivotItem, { headerText: "Engagements", itemCount: engagements.length },
                React.createElement(Stack, { tokens: { childrenGap: 12 }, styles: { root: { paddingTop: 12 } } },
                    React.createElement(Stack, { horizontal: true, horizontalAlign: "space-between", verticalAlign: "end", wrap: true, tokens: { childrenGap: 12 } },
                        React.createElement(ChoiceGroup, { label: "Engagement activity", selectedKey: engagementView, options: viewOptions, styles: { flexContainer: { display: 'flex', gap: 12 } }, onChange: (_event, option) => {
                                if (option) {
                                    setEngagementView(tableView(option.key));
                                    setEngagementSearch('');
                                }
                            } }),
                        React.createElement(SearchBox, { ariaLabel: "Search engagement table", placeholder: "Search engagements", value: engagementSearch, styles: { root: { width: 280 } }, onChange: (_event, value) => setEngagementSearch(value !== null && value !== void 0 ? value : '') })),
                    React.createElement("div", { style: { overflowX: 'auto' } },
                        React.createElement("table", { "data-testid": "engagements-table", "aria-label": `${engagementView} DSE engagements`, style: { width: '100%', borderCollapse: 'collapse' } },
                            React.createElement("thead", null,
                                React.createElement("tr", null, engagementHeaders.map((header) => (React.createElement("th", { key: header, style: thStyle }, header))))),
                            React.createElement("tbody", null, filteredEngagements.length === 0 ? (React.createElement("tr", null,
                                React.createElement("td", { colSpan: engagementHeaders.length, style: tdStyle },
                                    "No ",
                                    engagementView.toLowerCase(),
                                    " engagements found."))) : (filteredEngagements.map((engagement, index) => (React.createElement("tr", { key: `${engagement.demandSourceId}-${index}` },
                                React.createElement("td", { style: tdStyle }, engagement.title),
                                React.createElement("td", { style: tdStyle }, engagement.scheduleId),
                                React.createElement("td", { style: tdStyle }, engagement.scheduleName),
                                React.createElement("td", { style: tdStyle }, engagement.customer),
                                React.createElement("td", { style: tdStyle }, engagement.hoursSold),
                                React.createElement("td", { style: tdStyle }, engagement.hoursConsumed),
                                React.createElement("td", { style: tdStyle }, engagement.startDate),
                                React.createElement("td", { style: tdStyle }, engagement.endDate),
                                React.createElement("td", { style: tdStyle }, engagement.deliveryRate),
                                React.createElement("td", { style: tdStyle }, engagement.remainingDays),
                                React.createElement("td", { style: tdStyle }, engagement.owner),
                                React.createElement("td", { style: tdStyle }, engagement.district),
                                React.createElement("td", { style: tdStyle }, engagement.tamAlias),
                                React.createElement("td", { style: tdStyle }, engagement.contractId)))))))))))));
};
export default DSEBreakdown;
//# sourceMappingURL=DSEBreakdown.js.map